import { z } from "zod";

export const coworldRoleKeys = [
  "player",
  "commissioner",
  "grader",
  "diagnoser",
  "optimizer",
] as const;

const documentSchema = z.object({
  type: z.enum(["text", "uri"]),
  value: z.string(),
});
const roleFields = {
  id: z.string(),
  name: z.string(),
  description: z.string(),
  type: z.enum(coworldRoleKeys),
  run: z.array(z.string()).optional(),
};
const roleSchema = z.union([
  z.looseObject({
    ...roleFields,
    image: z.string(),
    file: z.string().nullish(),
  }),
  z.looseObject({
    ...roleFields,
    image: z.null().optional(),
    file: z.string(),
  }),
]);
const variantSchema = z.looseObject({
  id: z.string(),
  name: z.string(),
  description: z.string(),
});
const reporterSchema = z.union([
  z.object({ reporter: z.string() }),
  z.object({
    wasm: z.string(),
    id: z.string(),
    attributes: z.record(z.string(), z.unknown()),
  }),
]);

// Validate the fields browser consumers read while retaining unrelated manifest data.
export const manifestViewSchema = z.looseObject({
  game: z.looseObject({
    name: z.string(),
    version: z.string(),
    description: z.string(),
    owner: z.string(),
    runnable: z.looseObject({
      image: z.string(),
      run: z.array(z.string()).optional(),
      type: z.literal("game"),
    }),
    protocols: z.looseObject({
      player: documentSchema,
      global: documentSchema,
    }),
    docs: z
      .object({
        readme: documentSchema.nullish(),
        pages: z
          .array(
            z.object({
              id: z.string(),
              title: z.string(),
              content: documentSchema,
            }),
          )
          .optional(),
      })
      .nullish(),
    promo: z.object({ video_url: z.string() }).nullish(),
    achievements: z
      .array(
        z.object({
          id: z.string(),
          name: z.string(),
          description: z.string(),
          points: z.number(),
        }),
      )
      .nullish(),
  }),
  player: z.tuple([roleSchema], roleSchema),
  variants: z.tuple([variantSchema], variantSchema),
  reporter: z.array(reporterSchema).optional(),
  commissioner: z.array(roleSchema).optional(),
  grader: z.array(roleSchema).optional(),
  diagnoser: z.array(roleSchema).optional(),
  optimizer: z.array(roleSchema).optional(),
});

export type CoworldDoc = Readonly<z.infer<typeof documentSchema>>;
export type CoworldManifestView = z.infer<typeof manifestViewSchema>;
export type CoworldRoleKey = (typeof coworldRoleKeys)[number];
export type CoworldRoleSpec = z.infer<typeof roleSchema>;
export type CoworldReporterReference = Readonly<z.infer<typeof reporterSchema>>;
